/** Live Oracle Alert — quiet slide-in for founder/admin identities.
 * Polls the signal scanner; shows one alert at a time, calm and deliberate. */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Terminal, X } from "lucide-react";
import { api } from "./commandApi";
import { useCallsign } from "./useCallsign";
import { SEVERITY_STYLE } from "./AlertCenter";

const POLL_MS = 3 * 60 * 1000;

export default function OracleAlertsHost() {
  const identity = useCallsign();
  const [alert, setAlert] = useState(null);
  const shownRef = useRef(new Set());
  const navigate = useNavigate();
  const privileged = identity?.role === "owner" || identity?.role === "admin"
    || identity?.role === "editor";

  useEffect(() => {
    if (!privileged) return undefined;
    let timer;
    const poll = () => {
      api("/command/alerts")
        .then((d) => {
          const next = (d.alerts || []).find((a) => !shownRef.current.has(a.id));
          if (next) {
            setAlert((prev) => {
              if (prev) return prev;           // one at a time — never stack
              shownRef.current.add(next.id);
              return next;
            });
          }
        })
        .catch(() => {});
      timer = setTimeout(poll, POLL_MS);
    };
    poll();
    return () => clearTimeout(timer);
  }, [privileged]); // eslint-disable-line

  if (!privileged || !alert) return null;
  const sv = SEVERITY_STYLE[alert.severity] || SEVERITY_STYLE.info;

  const ack = (action) => {
    api("/command/alerts/act", { method: "post", data: { id: alert.id, action } }).catch(() => {});
    setAlert(null);
  };

  return (
    <div className="fixed bottom-5 right-5 z-[290] w-[340px] max-w-[calc(100vw-40px)] oracle-alert-in"
         data-testid="oracle-alert-toast">
      <div className="border bg-[#0a0806]/95 backdrop-blur-md shadow-2xl"
           style={{ borderColor: `${sv.color}66` }}>
        <div className="px-3 py-1.5 border-b flex items-center gap-2"
             style={{ borderColor: `${sv.color}33` }}>
          <Terminal size={11} style={{ color: sv.color }} />
          <span className="font-mono text-[9px] uppercase tracking-[0.35em] text-[#E4DFD3]">
            Oracle Alert
          </span>
          <span className="font-mono text-[8px] uppercase tracking-[0.2em] ml-auto"
                style={{ color: sv.color }}>
            {sv.label}
          </span>
          <button type="button" onClick={() => ack("read")} data-testid="oracle-alert-close"
                  className="text-[#5A554C] hover:text-[#E4DFD3]">
            <X size={12} />
          </button>
        </div>
        <div className="px-3 py-2.5">
          {identity?.callsign && (
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#E0A526]">
              {identity.callsign},
            </div>
          )}
          <div className="font-mono text-[11px] text-[#E4DFD3] mt-0.5">{alert.title}</div>
          <div className="font-mono text-[10px] text-[#A39E93] leading-relaxed mt-1">
            {alert.reason}
          </div>
          <div className="flex gap-3 mt-2">
            {alert.url && (
              <button type="button" data-testid="oracle-alert-review"
                      onClick={() => { ack("read"); navigate(alert.url); }}
                      className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#D92D20] hover:text-[#E4DFD3] transition-colors">
                Review →
              </button>
            )}
            <button type="button" data-testid="oracle-alert-dismiss"
                    onClick={() => ack("dismiss")}
                    className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#5A554C] hover:text-[#A39E93] transition-colors">
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
